import React, { useState } from "react";
import useReveal from "@/hooks/useReveal";
import { U } from "@/data/services";
import { insertRow, uploadFile, notifyForm } from "@/lib/forms";
import { useSettings } from "@/lib/SettingsContext";

const HERO_BG = U("1516450360452-9312f5e86fc7", 1600, 85);

interface FormState {
  firstName: string; lastName: string; email: string; phone: string;
  specialisation: string; experience: string; portfolio: string; skills: string;
  cv: File | null;
}
const BLANK: FormState = {
  firstName: "", lastName: "", email: "", phone: "",
  specialisation: "", experience: "", portfolio: "", skills: "", cv: null,
};

const Freelance: React.FC = () => {
  useReveal();
  const { settings } = useSettings();
  const [form, setForm] = useState<FormState>(BLANK);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; msg: string } | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true); setResult(null);
    try {
      // CV upload is best-effort — any storage failure must NOT block the submission.
      let cv_url: string | null = null;
      let cv_upload_error: string | null = null;
      if (form.cv) {
        try {
          const u = await uploadFile("uploads", "freelancers", form.cv);
          cv_url = u.url;
        } catch (uploadErr: any) {
          cv_upload_error = uploadErr?.message || String(uploadErr);
          console.error("[freelance] CV upload failed (continuing without file):", cv_upload_error);
        }
      }
      await insertRow("freelancer_submissions", {
        first_name: form.firstName,
        last_name: form.lastName,
        email: form.email,
        phone: form.phone,
        specialisation: form.specialisation,
        experience: form.experience,
        portfolio_url: form.portfolio,
        skills: form.skills,
        cv_url,
      });
      notifyForm({
        kind: "freelancer",
        to: settings.notification_email,
        data: { first_name: form.firstName, last_name: form.lastName, email: form.email, phone: form.phone, specialisation: form.specialisation, experience: form.experience, portfolio_url: form.portfolio, skills: form.skills, cv_url: cv_url || (cv_upload_error ? `(upload failed: ${cv_upload_error})` : "") },
      }).catch(() => {});
      setResult({ ok: true, msg: "Thank you — we have received your details and will reach out when a project matches your expertise." });
      setForm(BLANK);
    } catch (err: any) {
      setResult({ ok: false, msg: err?.message || "Something went wrong. Please email info@ideationav.com." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="iav-page" data-testid="freelance-page">
      <section className="iav-page-hero iav-hero-bg" style={{ backgroundImage: `url(${HERO_BG})` }}>
        <div className="iav-page-hero-overlay" />
        <div className="iav-page-hero-inner">
          <span className="iav-tag-line" data-reveal>Freelancers &amp; Crew</span>
          <h1 data-reveal>JOIN OUR<br /><span className="iav-accent">TALENT POOL.</span></h1>
          <p className="lead" data-reveal>Share your details and portfolio with us — we'll reach out when a project matches your expertise.</p>
        </div>
      </section>

      <div className="iav-2col">
        <div data-reveal>
          <h2>BUILT BY PASSIONATE<br /><span className="iav-accent">CREW.</span></h2>
          <p className="lead">We work with a wide network of skilled freelance technicians, operators and crew across Dubai and India. Share your details and portfolio with us — and we'll reach out when a project matches your expertise. Whether you are available for single days or extended contracts, we would love to have you in our database.</p>
          <div className="iav-pills">
            <span className="iav-pill">Audio</span>
            <span className="iav-pill">Lighting</span>
            <span className="iav-pill">Video &amp; LED</span>
            <span className="iav-pill">Rigging</span>
            <span className="iav-pill">SFX</span>
            <span className="iav-pill">Stage Mgmt</span>
            <span className="iav-pill">Production</span>
          </div>
        </div>

        <form className="iav-form" onSubmit={onSubmit} data-reveal data-testid="freelance-form">
          <div className="iav-form-row">
            <div className="iav-field"><label>First Name *</label><input required className="iav-input" value={form.firstName} onChange={e => setForm({ ...form, firstName: e.target.value })} /></div>
            <div className="iav-field"><label>Last Name *</label><input required className="iav-input" value={form.lastName} onChange={e => setForm({ ...form, lastName: e.target.value })} /></div>
          </div>
          <div className="iav-form-row">
            <div className="iav-field"><label>Email *</label><input required type="email" className="iav-input" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
            <div className="iav-field"><label>Phone *</label><input required type="tel" className="iav-input" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
          </div>
          <div className="iav-form-row">
            <div className="iav-field"><label>Specialisation *</label>
              <select required className="iav-select" value={form.specialisation} onChange={e => setForm({ ...form, specialisation: e.target.value })}>
                <option value="">Select…</option>
                <option>Audio Engineer (FOH / Monitor)</option>
                <option>Lighting Programmer / Operator</option>
                <option>LED &amp; Video Technician</option>
                <option>Rigging Technician</option>
                <option>SFX Operator</option>
                <option>Stage Manager</option>
                <option>Production Manager</option>
                <option>Other</option>
              </select>
            </div>
            <div className="iav-field"><label>Experience</label>
              <select className="iav-select" value={form.experience} onChange={e => setForm({ ...form, experience: e.target.value })}>
                <option value="">Select…</option>
                <option>Less than 1 year</option>
                <option>1 – 3 years</option>
                <option>3 – 5 years</option>
                <option>5 – 8 years</option>
                <option>8+ years</option>
              </select>
            </div>
          </div>
          <div className="iav-field"><label>Portfolio / LinkedIn / Website</label><input className="iav-input" placeholder="https://" value={form.portfolio} onChange={e => setForm({ ...form, portfolio: e.target.value })} /></div>
          <div className="iav-field"><label>Equipment &amp; Skills</label><textarea className="iav-textarea" placeholder="Tell us about the consoles, fixtures and systems you work with…" value={form.skills} onChange={e => setForm({ ...form, skills: e.target.value })} /></div>
          <div className="iav-field"><label>Upload CV / Portfolio (any file type, no size limit)</label>
            <label className="iav-file">
              <input type="file" onChange={e => setForm({ ...form, cv: e.target.files?.[0] || null })} />
              <div>{form.cv ? form.cv.name : "Drag & drop or click to upload"}</div>
              <small>Any file type — PDFs, images, ZIPs, presentations — no size limit</small>
            </label>
          </div>
          {result && <div className={`iav-form-msg ${result.ok ? "is-ok" : "is-err"}`} data-testid="freelance-result">{result.msg}</div>}
          <button type="submit" disabled={submitting} className="iav-btn iav-btn-primary" data-testid="freelance-submit">{submitting ? "Submitting…" : "Submit Application"}</button>
        </form>
      </div>
    </main>
  );
};
export default Freelance;
